<template>
    <SelectField
        v-model="modelValue.template_name"
        name="type"
        label="type"
        help="help_platform_email_template_type"
        :options="emailTemplateSelectOptions"
        :required="true"
    />

    <SelectField
        v-model="modelValue.language"
        name="language"
        label="language"
        help="help_platform_email_template_language"
        :options="languageSelectOptions"
        :required="true"
    />

    <SelectField
        v-model="modelValue.number_locale"
        name="number_locale"
        label="number_locale"
        help="help_platform_email_template_number_locale"
        :options="numberLocaleOptions"
    />

    <TextField
        v-model="modelValue.subject"
        name="subject"
        label="subject"
        help="help_platform_email_template_subject"
        :required="true"
    />

    <EditorField
        v-if="modelValue.template_name"
        v-model="modelValue.template_content"
        v-model:placeholders="placeholders"
        name="email_template_content"
        :required="true"
        label="content"
        help="help_platform_email_template_content"
    />
</template>
<script setup lang="ts">
import { EditorField, SelectField, TextField } from '@src/components'
import {
    Platform,
    PlatformEmailTemplate,
    PlatformEmailTemplateOption,
} from '@src/types'
import { getEmailTemplatesList } from '@src/utils/api/platforms'
import { useLanguageStore } from '@src/utils/stores/languages'
import { Ref, computed, onMounted, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()

const props = defineProps<{
    modelValue: Ref<PlatformEmailTemplate>
    platform: Ref<Platform>
}>()

const { modelValue, platform } = toRefs(props)
const emit = defineEmits(['update:modelValue', 'update:platform'])

const languageStore = useLanguageStore()
const languages = ref<{ label: string; value: string | null }[]>([])
const emailTemplates = ref<PlatformEmailTemplateOption[]>([])
const numberLocaleOptions = [
    { label: 'en-US  (1,500.00)', value: 'en-US' },
    { label: 'de-DE  (1.500,00)', value: 'de-DE' },
    { label: 'fr-FR  (1 500,00)', value: 'fr-FR' },
    { label: 'it-IT  (1.500,00)', value: 'it-IT' },
    { label: 'es-ES  (1.500,00)', value: 'es-ES' },
    { label: 'sr-RS  (1.500,00)', value: 'sr-RS' },
]

const availableEmailTemplates = computed(() => {
    if (platform.value.parent_platform_name !== null) {
        return emailTemplates.value.map((template) => {
            return template.value
        })
    } else {
        return emailTemplates.value
            .filter(
                (template) =>
                    (template.for_realm && platform.value.is_realm) ||
                    (template.for_platform && platform.value.is_platform)
            )
            .map((template) => {
                return template.value
            })
    }
})

const emailTemplateSelectOptions = computed(() => {
    return availableEmailTemplates.value.map((templateName: string) => {
        return {
            label: i18n.t('EMAIL_TEMPLATE_' + templateName.toUpperCase()),
            value: templateName,
        }
    })
})

const languageSelectOptions = computed(() => {
    const langs = languages.value.filter((lang) => {
        if (!modelValue.value.template_name) return false
        return !platform.value.email_templates.some((template) => {
            return (
                template.template_name === modelValue.value.template_name &&
                template.language === lang.value &&
                template.inherited !== true
            )
        })
    })
    if (!langs.length)
        return [
            {
                label: i18n.t('no_languages_available_for_this_email_template'),
                value: false,
            },
        ]
    return langs
})

const placeholders = computed(() => {
    if (!modelValue.value.template_name) return []

    if (
        [
            'basket_reminder_one',
            'basket_reminder_two',
            'pre_consumer_information',
        ].includes(modelValue.value.template_name)
    ) {
        return [
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'campaign_title',
                value: '{{campaign_title}}',
            },
            {
                key: 'asset_title',
                value: '{{asset_title}}',
            },
            {
                key: 'company_name',
                value: '{{company_name}}',
            },
            {
                key: 'full_name_or_company_name',
                value: '{{full_name_or_company_name}}',
            },
        ]
    }

    if (
        [
            'external_kyc_status_pending',
            'external_kyc_status_success',
            'kyc_status',
        ].includes(modelValue.value.template_name)
    ) {
        return [
            {
                key: 'status',
                value: '{{status}}',
            },
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
        ]
    }

    if (
        ['money_withdrawal_verification'].includes(
            modelValue.value.template_name
        )
    ) {
        return [
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
            {
                key: 'amount_in_cent',
                value: '{{amount_in_cent}}',
            },
            {
                key: 'amount',
                value: '{{amount}}',
            },
            {
                key: 'is_company',
                value: '{{is_company}}',
            },
            {
                key: 'verification_link',
                value: '{{verification_link}}',
            },
        ]
    }

    if (['voucher_received'].includes(modelValue.value.template_name)) {
        return [
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
            {
                key: 'voucher_amount',
                value: '{{voucher_amount}}',
            },
            {
                key: 'voucher_balance',
                value: '{{voucher_balance}}',
            },
        ]
    }

    if (['welcome'].includes(modelValue.value.template_name)) {
        return [
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
        ]
    }

    if (
        ['corporate_account_created', 'corporate_documents_submitted'].includes(
            modelValue.value.template_name
        )
    ) {
        return [
            {
                key: 'platform_name',
                value: '{{platform_name}}',
            },
            {
                key: 'platform_link',
                value: '{{platform_link}}',
            },
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
            {
                key: 'user_id',
                value: '{{user_id}}',
            },
            {
                key: 'natural_person_admin_link',
                value: '{{natural_person_admin_link}}',
            },
            {
                key: 'user_admin_link',
                value: '{{user_admin_link}}',
            },
            {
                key: 'timestamp',
                value: '{{timestamp}}',
            },
            {
                key: 'corporate_account_id',
                value: '{{corporate_account_id}}',
            },
            {
                key: 'corporate_account_admin_link',
                value: '{{corporate_account_admin_link}}',
            },
            { key: 'company_name', value: '{{company_name}}' },
            {
                key: 'registering_person_name',
                value: '{{registering_person_name}}',
            },
            {
                key: 'document_types_overview',
                value: '{{document_types_overview}}',
            },
            { key: 'admin_review_link', value: '{{admin_review_link}}' },
        ]
    }

    if (
        ['check_requested_membership_documents'].includes(
            modelValue.value.template_name
        )
    ) {
        return [
            {
                key: 'email',
                value: '{{email}}',
            },
            {
                key: 'download_url',
                value: '{{download_url}}',
            },
        ]
    }

    if (
        ['membership_document_accepted'].includes(
            modelValue.value.template_name
        )
    ) {
        return [
            {
                key: 'investor',
                value: '{{investor}}',
            },
        ]
    }

    if (
        ['membership_document_reminder'].includes(
            modelValue.value.template_name
        )
    ) {
        return [
            {
                key: 'investor',
                value: '{{investor_name}}',
            },
            {
                key: 'days',
                value: '{{days}}',
            },
        ]
    }

    if (
        ['savings_plan_payment_reminder'].includes(
            modelValue.value.template_name
        )
    ) {
        return [
            {
                key: 'balance',
                value: '{{balance}}',
            },
            {
                key: 'investment_amount',
                value: '{{investment_amount}}',
            },
            {
                key: 'difference_missing',
                value: '{{difference_missing}}',
            },
            {
                key: 'campaign_title',
                value: '{{campaign_title}}',
            },
        ]
    }

    if (
        [
            'acceptance_notice',
            'bank_account_information_reminder',
            'consumer_information',
            'gift_consumer_information',
            'kyc_reminder',
            'no_iban_reminder',
            'payment_confirmation',
            'payment_details',
            'payment_reminder',
            'signature_reminder',
            'signatures_regenerated',
            'signature_successful',
            'order_cancelled',
            'order_cancelled_investor_confirmation',
            'feedback_review',
        ].includes(modelValue.value.template_name)
    ) {
        return [
            {
                key: 'investment',
                value: '{{investment}}',
            },
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
            {
                key: 'investor_name',
                value: '{{investor_name}}',
            },
            {
                key: 'company_name',
                value: '{{company_name}}',
            },
            {
                key: 'campaign_title',
                value: '{{campaign_title}}',
            },
            {
                key: 'deposit_account_holder',
                value: '{{deposit_account_holder}}',
            },
            {
                key: 'deposit_account_holder_address',
                value: '{{deposit_account_holder_address}}',
            },
            {
                key: 'deposit_account_holder_country',
                value: '{{deposit_account_holder_country}}',
            },
            {
                key: 'investment_iban',
                value: '{{investment_iban}}',
            },
            {
                key: 'investment_bic',
                value: '{{investment_bic}}',
            },
            {
                key: 'order_id',
                value: '{{order_id}}',
            },
            {
                key: 'order_date',
                value: '{{order_date}}',
            },
            {
                key: 'reference_number',
                value: '{{reference_number}}',
            },
            {
                key: 'investment_amount',
                value: '{{investment_amount}}',
            },
            {
                key: 'voucher_amount',
                value: '{{voucher_amount}}',
            },
            {
                key: 'wallet_amount',
                value: '{{wallet_amount}}',
            },
            {
                key: 'total_amount_to_pay',
                value: '{{total_amount_to_pay}}',
            },
            {
                key: 'contract_type',
                value: '{{contract_type}}',
            },
            {
                key: 'interest_rate',
                value: '{{interest_rate}}',
            },
            {
                key: 'full_name_or_company_name',
                value: '{{full_name_or_company_name}}',
            },
            {
                key: 'initial_payment',
                value: '{{initial_payment}}',
            },
            {
                key: 'installment_rates',
                value: '{{installment_rates}}',
            },
            {
                key: 'installment_value',
                value: '{{installment_value}}',
            },
            {
                key: 'installment_type',
                value: '{{installment_type}}',
            },
            {
                key: 'voucher',
                value: '{{voucher}}',
            },
            {
                key: 'agio',
                value: '{{agio}}',
            },
            {
                key: 'signature_sproof_link',
                value: '{{signature_sproof_link}}',
            },
            {
                key: 'banking_app_link',
                value: '{{banking_app_link}}',
            },
            {
                key: 'epc_qr_code',
                value: '{{{epc_qr_code}}}',
            },
            {
                key: 'total_amount',
                value: '{{total_amount}}',
            },
            {
                key: 'pre_contract_reflection_date',
                value: '{{pre_contract_reflection_date}}',
            },
            {
                key: 'payment_url',
                value: '<a href="{{payment_url}}"> [Your Text Here] </a>',
            },
            {
                key: 'depot_owner',
                value: '{{depot_owner}}',
            },
            {
                key: 'depot_number',
                value: '{{depot_number}}',
            },
            {
                key: 'depot_bank',
                value: '{{depot_bank}}',
            },
            {
                key: 'depot_bic',
                value: '{{depot_bic}}',
            },
        ]
    }

    if (
        [
            'interest_payment_payout_received',
            'interest_payment_announcement',
        ].includes(modelValue.value.template_name)
    ) {
        return [
            {
                key: 'company_name',
                value: '{{company_name}}',
            },
            {
                key: 'campaign_title',
                value: '{{campaign_title}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
            {
                key: 'full_name_or_company_name',
                value: '{{full_name_or_company_name}}',
            },
            {
                key: 'investor_name',
                value: '{{investor_name}}',
            },
            {
                key: 'order_id',
                value: '{{order_id}}',
            },
            {
                key: 'paid_total_gross',
                value: '{{paid_total_gross}}',
            },
            {
                key: 'paid_total_net',
                value: '{{paid_total_net}}',
            },
            {
                key: 'paid_interest',
                value: '{{paid_interest}}',
            },
            {
                key: 'paid_interest_excluding_bonus',
                value: '{{paid_interest_excluding_bonus}}',
            },
            {
                key: 'paid_interest_bonus',
                value: '{{paid_interest_bonus}}',
            },
            {
                key: 'paid_clearance',
                value: '{{paid_clearance}}',
            },
            {
                key: 'paid_rest',
                value: '{{paid_rest}}',
            },
            {
                key: 'paid_rest_clearance',
                value: '{{paid_rest_clearance}}',
            },
            {
                key: 'paid_rest_interest',
                value: '{{paid_rest_interest}}',
            },
            {
                key: 'reference_number',
                value: '{{reference_number}}',
            },
            {
                key: 'total_amount',
                value: '{{total_amount}}',
            },
            {
                key: 'total_amount_to_pay',
                value: '{{total_amount_to_pay}}',
            },
            {
                key: 'voucher_amount',
                value: '{{voucher_amount}}',
            },
            {
                key: 'wallet_amount',
                value: '{{wallet_amount}}',
            },
            {
                key: 'paid_bonus',
                value: '{{paid_bonus}}',
            },
            {
                key: 'interest_rate',
                value: '{{interest_rate}}',
            },
            {
                key: 'interest_bonus',
                value: '{{interest_bonus}}',
            },
            {
                key: 'paid_interest_total',
                value: '{{paid_interest_total}}',
            },
            {
                key: 'paid_bonus_total',
                value: '{{paid_bonus_total}}',
            },
            {
                key: 'previous_interest_date',
                value: '{{previous_interest_date}}',
            },
            {
                key: 'current_interest_date',
                value: '{{current_interest_date}}',
            },
            {
                key: 'paid_clearance_total',
                value: '{{paid_clearance_total}}',
            },
            {
                key: 'investment_acceptance_date',
                value: '{{investment_acceptance_date}}',
            },
            {
                key: 'interest_calculation_start_date',
                value: '{{interest_calculation_start_date}}',
            },
            {
                key: 'investor_address',
                value: '{{investor_address}}',
            },
            {
                key: 'investor_zip',
                value: '{{investor_zip}}',
            },
            {
                key: 'investor_city',
                value: '{{investor_city}}',
            },
            {
                key: 'investor_country',
                value: '{{investor_country}}',
            },
            {
                key: 'investment',
                value: '{{investment}}',
            },
            {
                key: 'investment_amount',
                value: '{{investment_amount}}',
            },
            {
                key: 'deposit_account_holder',
                value: '{{deposit_account_holder}}',
            },
            {
                key: 'deposit_account_holder_address',
                value: '{{deposit_account_holder_address}}',
            },
            {
                key: 'deposit_account_holder_country',
                value: '{{deposit_account_holder_country}}',
            },
            {
                key: 'investment_bic',
                value: '{{investment_bic}}',
            },
            {
                key: 'investment_iban',
                value: '{{investment_iban}}',
            },
            {
                key: 'contract_type',
                value: '{{contract_type}}',
            },
            {
                key: 'issuer',
                value: '{{issuer}}',
            },
            {
                key: 'gross_interest',
                value: '{{gross_interest}}',
            },
            {
                key: 'net_interest',
                value: '{{net_interest}}',
            },
            {
                key: 'tax_amount',
                value: '{{tax_amount}}',
            },
            {
                key: 'tax_rate',
                value: '{{tax_rate}}',
            },
            {
                key: 'tax_details',
                value: '{{tax_details}}',
            },
        ]
    }

    if ('nyala_registry_file' === modelValue.value.template_name) {
        return [
            {
                key: 'investment',
                value: '{{investment}}',
            },
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
            {
                key: 'investor_name',
                value: '{{investor_name}}',
            },
            {
                key: 'company_name',
                value: '{{company_name}}',
            },
            {
                key: 'campaign_title',
                value: '{{campaign_title}}',
            },
            {
                key: 'deposit_account_holder',
                value: '{{deposit_account_holder}}',
            },
            {
                key: 'deposit_account_holder_address',
                value: '{{deposit_account_holder_address}}',
            },
            {
                key: 'deposit_account_holder_country',
                value: '{{deposit_account_holder_country}}',
            },
            {
                key: 'investment_iban',
                value: '{{investment_iban}}',
            },
            {
                key: 'investment_bic',
                value: '{{investment_bic}}',
            },
            {
                key: 'order_id',
                value: '{{order_id}}',
            },
            {
                key: 'order_date',
                value: '{{order_date}}',
            },
            {
                key: 'reference_number',
                value: '{{reference_number}}',
            },
            {
                key: 'investment_amount',
                value: '{{investment_amount}}',
            },
            {
                key: 'voucher_amount',
                value: '{{voucher_amount}}',
            },
            {
                key: 'wallet_amount',
                value: '{{wallet_amount}}',
            },
            {
                key: 'total_amount_to_pay',
                value: '{{total_amount_to_pay}}',
            },
            {
                key: 'contract_type',
                value: '{{contract_type}}',
            },
            {
                key: 'interest_rate',
                value: '{{interest_rate}}',
            },
            {
                key: 'full_name_or_company_name',
                value: '{{full_name_or_company_name}}',
            },
            {
                key: 'initial_payment',
                value: '{{initial_payment}}',
            },
            {
                key: 'installment_rates',
                value: '{{installment_rates}}',
            },
            {
                key: 'installment_value',
                value: '{{installment_value}}',
            },
            {
                key: 'installment_type',
                value: '{{installment_type}}',
            },
            {
                key: 'voucher',
                value: '{{voucher}}',
            },
            {
                key: 'agio',
                value: '{{agio}}',
            },
            {
                key: 'signature_sproof_link',
                value: '{{signature_sproof_link}}',
            },
            {
                key: 'banking_app_link',
                value: '{{banking_app_link}}',
            },
            {
                key: 'epc_qr_code',
                value: '{{{epc_qr_code}}}',
            },
            {
                key: 'total_amount',
                value: '{{total_amount}}',
            },
            {
                key: 'pre_contract_reflection_date',
                value: '{{pre_contract_reflection_date}}',
            },
            {
                key: 'nyala_registry_file_name',
                value: '{{nyala_registry_file_name}}',
            },
            {
                key: 'asset_title',
                value: '{{asset_title}}',
            },
            {
                key: 'total_invested_amount',
                value: '{{total_invested_amount}}',
            },
            {
                key: 'total_amount_of_shares',
                value: '{{total_amount_of_shares}}',
            },
            {
                key: 'total_amount_of_shares_per_contract',
                value: '{{total_amount_of_shares_per_contract}}',
            },
        ]
    }

    if ('newsletter_consent' === modelValue.value.template_name) {
        return [
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
            {
                key: 'verification_link',
                value: '{{verification_link}}',
            },
        ]
    }

    if ('kyc_accepted' === modelValue.value.template_name) {
        return [
            {
                key: 'first_name',
                value: '{{first_name}}',
            },
            {
                key: 'last_name',
                value: '{{last_name}}',
            },
            {
                key: 'email',
                value: '{{email}}',
            },
        ]
    }

    if (modelValue.value.template_name.toString().startsWith('sm_')) {
        return [
            { key: 'action_url', value: '{{action_url}}' },
            { key: 'asset_title', value: '{{asset_title}}' },
            { key: 'expires_at', value: '{{expires_at}}' },
            { key: 'failure_reason', value: '{{failure_reason}}' },
            { key: 'first_name', value: '{{first_name}}' },
            { key: 'last_name', value: '{{last_name}}' },
            { key: 'marketplace_url', value: '{{marketplace_url}}' },
            { key: 'payment_reference', value: '{{payment_reference}}' },
            { key: 'price_per_unit', value: '{{price_per_unit}}' },
            { key: 'quantity', value: '{{quantity}}' },
            { key: 'seller_bic', value: '{{seller_bic}}' },
            { key: 'seller_iban', value: '{{seller_iban}}' },
            { key: 'seller_name', value: '{{seller_name}}' },
            { key: 'total_price', value: '{{total_price}}' },
        ]
    }

    return []
})

watch(modelValue.value, (value) => {
    emit('update:modelValue', value)
})

onMounted(async () => {
    languages.value = languageStore.languages.map((lang) => {
        return {
            label: i18n.t(lang.value) || lang.value,
            value: lang.value,
        }
    })
    const emailTemplatesResponse = await getEmailTemplatesList()
    emailTemplates.value = emailTemplatesResponse.data.email_templates
})
</script>
